import { inject, Injectable } from '@angular/core';
import { ApiResponseModel, ApiService } from '@solcre-org/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment.local';
import { PortCallPdaModel } from '../models/port-call-pda.model';
import { PortTariffLineTypes } from '../enums/port-tariff-line-types.enum';
import { PortTariffLineUnitTypes } from '../enums/port-tariff-line-unit-types.enum';
import { PortTariffLineVesselProperties } from '../enums/port-tariff-line-vessel-properties.enum';

@Injectable({
  providedIn: 'root',
})
export class PortCallPdaService {
  private apiService: ApiService = inject(ApiService);

  public fetchPda(portCallId: string | number): Observable<PortCallPdaModel> {
    return this.apiService.fetchData(this.pdaPath(portCallId)).pipe(
      map((response: ApiResponseModel) => new PortCallPdaModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public createPda(portCallId: string | number): Observable<PortCallPdaModel> {
    return this.apiService.createObj(this.pdaPath(portCallId), {}).pipe(
      map((response: ApiResponseModel) => new PortCallPdaModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public recalculate(pdaId: string | number, refreshTariffLines = false): Observable<PortCallPdaModel> {
    return this.apiService.createObj(`${this.pdaItemPath(pdaId)}/recalculate`, { refreshTariffLines }).pipe(
      map((response: ApiResponseModel) => new PortCallPdaModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public approve(pdaId: string | number): Observable<PortCallPdaModel> {
    return this.apiService.createObj(`${this.pdaItemPath(pdaId)}/approve`, {}).pipe(
      map((response: ApiResponseModel) => new PortCallPdaModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public addManualLine(
    pdaId: string | number,
    payload: {
      concept: string;
      lineType: PortTariffLineTypes;
      rateValue: string;
      unitType: PortTariffLineUnitTypes.FIXED;
      vesselProperty: PortTariffLineVesselProperties.NONE;
      expenseCategory: string;
      operationDirection: string;
      sortOrder?: number;
    }
  ): Observable<PortCallPdaModel> {
    return this.apiService.createObj(`${this.pdaItemPath(pdaId)}/lines`, payload).pipe(
      map((response: ApiResponseModel) => new PortCallPdaModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public overrideLine(
    lineId: string | number,
    payload: { manualAmountUsd: string; overrideReason?: string }
  ): Observable<PortCallPdaModel> {
    return this.apiService.partialUpdateObj(
      environment.API.PORT_CALL_PDA_LINES_URI,
      String(lineId),
      payload,
      false,
      { 'Content-Type': 'application/json' }
    ).pipe(
      map((response: ApiResponseModel) => new PortCallPdaModel().fromJSON(this.extractObject(response.data)))
    );
  }

  public resetLine(lineId: string | number): Observable<PortCallPdaModel> {
    return this.apiService.createObj(`${environment.API.PORT_CALL_PDA_LINES_URI}/${lineId}/reset`, {}).pipe(
      map((response: ApiResponseModel) => new PortCallPdaModel().fromJSON(this.extractObject(response.data)))
    );
  }

  private pdaPath(portCallId: string | number): string {
    return environment.API.PORT_CALL_PDA_URI.replace(':id', String(portCallId));
  }

  private pdaItemPath(pdaId: string | number): string {
    return `${environment.API.PORT_CALL_PDAS_URI}/${pdaId}`;
  }

  private extractObject(data: any): any {
    if (data && typeof data === 'object' && 'data' in data) {
      return data.data;
    }
    return data;
  }
}
